import { supabase } from '@/config/supabase';
import type {
  ExamAttempt,
  ExamDifficulty,
  ExamEligibility,
  ExamMCQQuestion,
  ExamResult,
  ExamStats,
  UserAnswerIndex,
} from '@/types/exam';
import { aiExamQuestionGenerator } from './aiExamQuestionGenerator';

const PASSING_SCORE = 75; // 75% required to pass
const ELIGIBLE_COMPLETION = 80; // 80% roadmap completion required to unlock exam
const ADMIN_EMAIL = 'dynamicnabid@gmail.com'; // Admin can take any exam

// Map difficulty to number of questions
const QUESTION_COUNTS: Record<ExamDifficulty, number> = {
  beginner: 30,
  intermediate: 60,
  expert: 100,
};

// Default days per roadmap intensity level
const DEFAULT_DAYS_BY_INTENSITY: Record<string, number> = {
  Beginner: 28,
  beginner: 28,
  Intermediate: 84,
  intermediate: 84,
  Advanced: 168,
  advanced: 168,
  Expert: 168,
  expert: 168,
};

export const examService = {
  /**
   * Check if user is eligible to take exam for a roadmap
   * Admins can always take exams (no 75% requirement)
   */
  async checkExamEligibility(
    userId: string,
    roadmapId: string,
    userEmail?: string
  ): Promise<ExamEligibility> {
    try {
      const isAdmin = userEmail === ADMIN_EMAIL;

      // Get roadmap details - roadmaps use 'intensity' field, not 'level'
      // Note: lang and meta columns may not exist, so we fetch without them and add fallbacks
      const { data: roadmap, error: roadmapError } = await supabase
        .from('roadmaps')
        .select('id, title, intensity, months')
        .eq('id', roadmapId)
        .maybeSingle();

      if (roadmapError) {
        console.error('Error fetching roadmap:', roadmapError);
        throw new Error(`Error fetching roadmap: ${roadmapError.message}`);
      }

      if (!roadmap) {
        console.warn(`Roadmap not found: ${roadmapId}`);
        throw new Error(`Roadmap not found: ${roadmapId}`);
      }

      // Get the total days based on roadmap intensity (fixed values)
      // Beginner: 28, Intermediate: 84, Expert: 168
      const totalDays = DEFAULT_DAYS_BY_INTENSITY[roadmap.intensity || 'Beginner'] || 28;

      // Get all articles to check user progress
      const { data: allArticles, error: articlesError } = await supabase
        .from('articles')
        .select('id, day_id')
        .eq('roadmap_id', roadmapId);

      if (articlesError) {
        console.error('Error fetching articles:', articlesError);
      }

      // Get user progress on this roadmap from user_roadmap_progress table
      // NOTE: The UNIQUE constraint is on (email, roadmap_id) - email is the primary identifier
      let userProgress: any = null;

      if (!userEmail) {
        console.warn('No user email provided - cannot fetch progress');
      } else {
        // Query by email and roadmap_id (the unique constraint combination)
        const { data: progressByEmail, error: errorByEmail } = await supabase
          .from('user_roadmap_progress')
          .select('current_day')
          .eq('email', userEmail)
          .eq('roadmap_id', roadmapId)
          .maybeSingle();

        if (errorByEmail) {
          console.error(`Error fetching progress for ${userEmail} on ${roadmapId}:`, errorByEmail);
        } else if (progressByEmail) {
          userProgress = progressByEmail;
          console.log(`✓ Found progress for ${userEmail}: current_day=${userProgress.current_day}`);
        }
      }

      // Get completed articles for this roadmap (same as dashboard does)
      // Note: user_article_progress uses completed_at (timestamp) not a boolean
      const { data: completedArticles = [], error: completedArticlesError } = await supabase
        .from('user_article_progress')
        .select('article_id, roadmap_id, completed_at')
        .eq('roadmap_id', roadmapId)
        .not('completed_at', 'is', null); // Filter for articles that have been completed

      if (completedArticlesError) {
        console.error('Error fetching completed articles:', completedArticlesError);
      }

      // Use the same calculation as dashboard: count of completed articles
      const completedDays = (completedArticles || []).length;
      console.log(`Exam check: user=${userEmail}, roadmap=${roadmapId}, completedDays=${completedDays}, totalDays=${totalDays}`);
      const completionPercentage = totalDays > 0 ? (completedDays / totalDays) * 100 : 0;
      const daysRequiredForUnlock = Math.ceil(totalDays * (ELIGIBLE_COMPLETION / 100));

      // Check eligibility
      const isEligible = isAdmin || completionPercentage >= ELIGIBLE_COMPLETION;

      // Check last exam attempt
      const { data: lastAttempt, error: attemptError } = await supabase
        .from('exam_attempts')
        .select('id, score_percentage, passed, completed_at')
        .eq('user_id', userId)
        .eq('roadmap_id', roadmapId)
        .order('completed_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (attemptError) {
        console.error('Error fetching exam attempt:', attemptError);
      }

      // Map intensity to ExamDifficulty
      const difficultyMap: Record<string, ExamDifficulty> = {
        Beginner: 'beginner',
        Intermediate: 'intermediate',
        Advanced: 'expert',
        Expert: 'expert',
      };

      return {
        roadmap_id: roadmapId,
        roadmap_name: roadmap.title,
        title: roadmap.title,
        roadmap_difficulty: (difficultyMap[roadmap.intensity] || 'beginner') as ExamDifficulty,
        intensity: roadmap.intensity,
        lang: 'en', // Default fallback if not in database
        months: roadmap.months,
        meta: {
          studyTime: 2, // Default 2 hours per day
        },
        total_days: totalDays,
        completed_days: completedDays,
        completion_percentage: completionPercentage,
        is_eligible: isEligible,
        has_taken_exam: !!lastAttempt,
        last_exam_result: lastAttempt as ExamAttempt | undefined,
        days_required_for_unlock: daysRequiredForUnlock,
      };
    } catch (error) {
      console.error('Error checking exam eligibility:', error);
      throw error;
    }
  },

  /**
   * Get random exam questions for a difficulty level
   * If questions don't exist, AI generates them based on roadmap content
   */
  async getExamQuestions(
    roadmapId: string,
    difficulty: ExamDifficulty
  ): Promise<ExamMCQQuestion[]> {
    try {
      const questionCount = QUESTION_COUNTS[difficulty];

      // Try to fetch existing questions first (no random() - Supabase doesn't support it)
      const { data: questions, error } = await supabase
        .from('exam_questions')
        .select('*')
        .eq('roadmap_id', roadmapId)
        .eq('difficulty', difficulty)
        .limit(questionCount * 2); // Fetch extra to account for shuffling

      if (error) throw error;

      // If we have enough questions, shuffle and return them
      if (questions && questions.length >= questionCount) {
        // Shuffle array using Fisher-Yates algorithm
        const shuffled = [...questions].sort(() => Math.random() - 0.5);
        return shuffled.slice(0, questionCount) as ExamMCQQuestion[];
      }

      // If insufficient questions, use AI to generate them
      console.log(
        `Generating ${questionCount} ${difficulty} questions for roadmap ${roadmapId}`
      );
      const generatedQuestions =
        await aiExamQuestionGenerator.generateExamQuestions(
          roadmapId,
          difficulty,
          questionCount
        );

      // Add id and roadmap_id to generated questions for consistency
      const questionsWithIds = generatedQuestions.map((q) => ({
        id: `gen-${Date.now()}-${Math.random()}`, // Temporary ID
        roadmap_id: roadmapId,
        ...q,
      }));

      return questionsWithIds as ExamMCQQuestion[];
    } catch (error) {
      console.error('Error fetching/generating exam questions:', error);
      throw error;
    }
  },

  /**
   * Submit exam and calculate score
   */
  async submitExam(
    userId: string,
    roadmapId: string,
    difficulty: ExamDifficulty,
    questions: ExamMCQQuestion[],
    userAnswers: UserAnswerIndex[],
    timeSpentSeconds?: number
  ): Promise<{ examResult: ExamResult; examAttemptId: string }> {
    try {
      // Calculate score
      let correctCount = 0;
      for (let i = 0; i < questions.length; i++) {
        if (userAnswers[i] === questions[i].correctAnswer) {
          correctCount++;
        }
      }

      const scorePercentage = (correctCount / questions.length) * 100;
      const passed = scorePercentage >= PASSING_SCORE;

      // Get roadmap info
      const { data: roadmapData, error: roadmapError } = await supabase
        .from('roadmaps')
        .select('title')
        .eq('id', roadmapId)
        .single();

      if (roadmapError) throw roadmapError;

      // Store exam attempt
      const { data: examAttemptData, error: examError } = await supabase
        .from('exam_attempts')
        .insert([
          {
            user_id: userId,
            roadmap_id: roadmapId,
            exam_difficulty: difficulty,
            roadmap_name: roadmapData.title,
            started_at: new Date(Date.now() - (timeSpentSeconds || 0) * 1000).toISOString(),
            completed_at: new Date().toISOString(),
            total_questions: questions.length,
            attempted_questions: userAnswers.filter((a) => a !== null).length,
            correct_answers: correctCount,
            score_percentage: Math.round(scorePercentage),
            passed,
            user_answers: userAnswers,
            time_spent_seconds: timeSpentSeconds,
          },
        ])
        .select()
        .single();

      if (examError) throw examError;

      // If passed, generate certificate
      if (passed) {
        await examService.generateCertificate(
          userId,
          examAttemptData.id,
          roadmapId,
          difficulty
        );
      }

      return {
        examResult: {
          passed,
          score_percentage: Math.round(scorePercentage),
          correct_answers: correctCount,
          total_questions: questions.length,
          message: passed
            ? `🎉 Congratulations! You passed with ${Math.round(scorePercentage)}%`
            : `You scored ${Math.round(scorePercentage)}%. You need 75% to pass. Try again!`,
        },
        examAttemptId: examAttemptData.id,
      };
    } catch (error) {
      console.error('Error submitting exam:', error);
      throw error;
    }
  },

  /**
   * Generate certificate for passed exam
   */
  async generateCertificate(
    userId: string,
    examAttemptId: string,
    roadmapId: string,
    difficulty: ExamDifficulty
  ): Promise<string> {
    try {
      const { data: examAttempt } = await supabase
        .from('exam_attempts')
        .select('*')
        .eq('id', examAttemptId)
        .single();

      const { data: userData } = await supabase
        .from('profiles')
        .select('full_name, email')
        .eq('id', userId)
        .single();

      const { data: roadmapData } = await supabase
        .from('roadmaps')
        .select('title')
        .eq('id', roadmapId)
        .single();

      // Generate unique credential code
      const credentialCode = `CERT-${userId.substring(0, 8)}-${Date.now()}`.toUpperCase();

      // Store certificate
      const { data: certificateData, error: certError } = await supabase
        .from('certificates')
        .insert([
          {
            exam_attempt_id: examAttemptId,
            user_id: userId,
            user_name: userData?.full_name || 'User',
            user_email: userData?.email || '',
            roadmap_name: roadmapData?.title || 'Roadmap',
            roadmap_difficulty: difficulty,
            score_percentage: examAttempt.score_percentage,
            issued_date: new Date().toISOString(),
            credential_code: credentialCode,
          },
        ])
        .select()
        .single();

      if (certError) throw certError;

      return certificateData.id;
    } catch (error) {
      console.error('Error generating certificate:', error);
      throw error;
    }
  },

  /**
   * Get user's exam statistics
   */
  async getExamStats(userId: string): Promise<ExamStats> {
    try {
      const { data: attempts } = await supabase
        .from('exam_attempts')
        .select('*')
        .eq('user_id', userId);

      if (!attempts || attempts.length === 0) {
        return {
          total_exams_taken: 0,
          exams_passed: 0,
          exams_failed: 0,
          average_score: 0,
          certificates_earned: 0,
          by_difficulty: {
            beginner: { taken: 0, passed: 0, average_score: 0 },
            intermediate: { taken: 0, passed: 0, average_score: 0 },
            expert: { taken: 0, passed: 0, average_score: 0 },
          },
        };
      }

      const stats: ExamStats = {
        total_exams_taken: attempts.length,
        exams_passed: attempts.filter((a) => a.passed).length,
        exams_failed: attempts.filter((a) => !a.passed).length,
        average_score: Math.round(
          attempts.reduce((sum, a) => sum + a.score_percentage, 0) / attempts.length
        ),
        certificates_earned: 0,
        by_difficulty: {
          beginner: { taken: 0, passed: 0, average_score: 0 },
          intermediate: { taken: 0, passed: 0, average_score: 0 },
          expert: { taken: 0, passed: 0, average_score: 0 },
        },
      };

      // Calculate by difficulty
      const difficultyStats = {
        beginner: { attempts: [] as ExamAttempt[], passed: 0 },
        intermediate: { attempts: [] as ExamAttempt[], passed: 0 },
        expert: { attempts: [] as ExamAttempt[], passed: 0 },
      };

      attempts.forEach((attempt) => {
        const difficulty = attempt.exam_difficulty as ExamDifficulty;
        difficultyStats[difficulty].attempts.push(attempt);
        if (attempt.passed) {
          difficultyStats[difficulty].passed++;
        }
      });

      Object.entries(difficultyStats).forEach(([diff, data]) => {
        const d = diff as ExamDifficulty;
        stats.by_difficulty[d].taken = data.attempts.length;
        stats.by_difficulty[d].passed = data.passed;
        stats.by_difficulty[d].average_score =
          data.attempts.length > 0
            ? Math.round(
                data.attempts.reduce((sum, a) => sum + a.score_percentage, 0) /
                  data.attempts.length
              )
            : 0;
      });

      // Get certificates count
      const { data: certificates } = await supabase
        .from('certificates')
        .select('id')
        .eq('user_id', userId);

      stats.certificates_earned = certificates?.length || 0;

      return stats;
    } catch (error) {
      console.error('Error getting exam stats:', error);
      throw error;
    }
  },

  /**
   * Get user's certificates
   */
  async getUserCertificates(userId: string) {
    try {
      const { data: certificates, error } = await supabase
        .from('certificates')
        .select('*')
        .eq('user_id', userId)
        .order('issued_date', { ascending: false });

      if (error) throw error;
      return certificates || [];
    } catch (error) {
      console.error('Error fetching certificates:', error);
      throw error;
    }
  },

  /**
   * Get all eligible exams for user
   * Shows exams ONLY for roadmaps user has created or viewed (bookmarked)
   * For admins: all roadmaps are eligible
   * For regular users: only their own roadmaps + 80% completion to take exam
   */
  async getEligibleExams(userId: string, userEmail?: string): Promise<ExamEligibility[]> {
    try {
      console.log('getEligibleExams called for:', { userId, userEmail });
      const isAdmin = userEmail === ADMIN_EMAIL;

      // Determine which roadmaps to show based on user type
      let roadmapIds: string[] = [];

      if (isAdmin) {
        // Admins see all roadmaps
        const { data: allRoadmaps } = await supabase
          .from('roadmaps')
          .select('id');
        roadmapIds = allRoadmaps?.map((r: any) => r.id) || [];
        console.log('Admin - all roadmaps found:', roadmapIds.length);
      } else {
        // Regular users see only roadmaps they created or viewed
        // Get user's roadmaps from user_roadmaps table (created or viewed/bookmarked)
        // Query by both user_id and email to capture all user's roadmaps
        let allUserRoadmaps: any[] = [];

        // Query by user_id
        if (userId) {
          const { data: roadmapsByUserId, error: errorByUserId } = await supabase
            .from('user_roadmaps')
            .select('roadmap_id')
            .eq('user_id', userId);

          if (errorByUserId) {
            console.error('Error fetching user roadmaps by user_id:', errorByUserId);
          } else {
            allUserRoadmaps = [...allUserRoadmaps, ...(roadmapsByUserId || [])];
          }
        }

        // Query by email
        if (userEmail) {
          const { data: roadmapsByEmail, error: errorByEmail } = await supabase
            .from('user_roadmaps')
            .select('roadmap_id')
            .eq('email', userEmail);

          if (errorByEmail) {
            console.error('Error fetching user roadmaps by email:', errorByEmail);
          } else {
            allUserRoadmaps = [...allUserRoadmaps, ...(roadmapsByEmail || [])];
          }
        }

        // Remove duplicates
        roadmapIds = [...new Set(allUserRoadmaps.map((r: any) => r.roadmap_id))];
        console.log('User roadmaps found:', roadmapIds.length, 'Roadmap IDs:', roadmapIds);
      }

      if (roadmapIds.length === 0) {
        console.log('No user roadmaps available');
        return [];
      }

      // Get exam info for each user's roadmap
      const examList: ExamEligibility[] = [];

      for (const roadmapId of roadmapIds) {
        try {
          const eligibility = await examService.checkExamEligibility(userId, roadmapId, userEmail);
          examList.push(eligibility);
          console.log(`Roadmap ${roadmapId}: eligible=${eligibility.is_eligible}, completion=${eligibility.completion_percentage}%, required=${eligibility.days_required_for_unlock || 'N/A'} days`);
        } catch (err) {
          console.warn(`Failed to get exam info for roadmap ${roadmapId}:`, err);
        }
      }

      console.log('Total roadmaps for display:', examList.length);
      return examList;
    } catch (error) {
      console.error('Error getting exams:', error);
      throw error;
    }
  },

  /**
   * Get exam attempt count for a user on a specific roadmap
   * Returns object with: total attempts, remaining attempts, can_attempt
   * Max attempts per roadmap: 3
   */
  async getExamAttemptCount(
    userId: string,
    roadmapId: string
  ): Promise<{
    total_attempts: number;
    remaining_attempts: number;
    can_attempt: boolean;
    max_attempts: number;
  }> {
    try {
      const MAX_ATTEMPTS = 3;

      // Get all exam attempts for this user on this roadmap
      const { data: attempts, error } = await supabase
        .from('exam_attempts')
        .select('id, completed_at')
        .eq('user_id', userId)
        .eq('roadmap_id', roadmapId)
        .order('completed_at', { ascending: false });

      if (error) throw error;

      const totalAttempts = attempts?.length || 0;
      const remainingAttempts = Math.max(0, MAX_ATTEMPTS - totalAttempts);
      const canAttempt = remainingAttempts > 0;

      return {
        total_attempts: totalAttempts,
        remaining_attempts: remainingAttempts,
        can_attempt: canAttempt,
        max_attempts: MAX_ATTEMPTS,
      };
    } catch (error) {
      console.error('Error getting attempt count:', error);
      throw error;
    }
  },
};
